import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from '@heroicons/react/24/outline';

interface Training {
  id: number;
  name: string;
  position: string;
  company: string;
  testimonial: string;
  image: string;
  rating?: number;
  duration?: string;
}

const trainings: Training[] = [
  {
    id: 1,
    name: "Diplome en gestion de projet",
    position: "Cursus",
    company: "Academie Canadienne de Management et Technologies",
    testimonial: "Gestion de projet | Gestion de Risque | Gestion de la Qualité | Contrôle des couts.",
    image: "/edu3.png",
    rating: 2,
    duration: "2020-2021"
  },
  {
    id: 2,
    name: "Master Professionnelle en Informatique",
    position: "Cursus",
    company: "Université T.S.I Madagascar",
    testimonial: "Ingenierie Applicative | Cycle de Devellopement d'une application Web",
    image: "/edu3.png",
    rating: 5,
    duration: "2016-2017"
  },
  {
    id: 3,
    name: "Licence Professionnelle en Information",
    position: "Cursus",
    company: "Université T.S.I Madagascar",
    testimonial: "Administration système et Base de donnée",
    image: "/edu3.png",
    rating: 3,
    duration: "2014 - 2016"
  }
];

const Trainings: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = useCallback((newDirection: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      const nextPage = prevIndex + newDirection;
      if (nextPage < 0) return trainings.length - 1;
      if (nextPage >= trainings.length) return 0;
      return nextPage;
    });
  }, [isAnimating]);

  useEffect(() => {
    const autoSlide = () => {
      paginate(1);
    };
    const timer = setInterval(autoSlide, 10000);
    return () => clearInterval(timer);
  }, [paginate]);

  return (
    <section id="trainings" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-12">Educations</h2>
        <div className="relative h-[400px]">
          <AnimatePresence initial={false} custom={direction} mode="wait" onExitComplete={() => setIsAnimating(false)}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="absolute w-full"
            >
              <div className="card bg-base-100 shadow-xl max-w-2xl mx-auto">
                <div className="card-body items-center text-center p-8">
                  <div className="avatar mb-6">
                    <div className="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                      <img
                        src={trainings[currentIndex].image}
                        alt={trainings[currentIndex].name}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(trainings[currentIndex].rating || 5)].map((_, i) => (
                      <StarIcon key={i} className="h-5 w-5 text-warning fill-current" />
                    ))}
                  </div>
                  <p className="text-lg italic mb-6 leading-relaxed">
                    "{trainings[currentIndex].testimonial}"
                  </p>
                  <h3 className="font-bold text-xl mb-1">{trainings[currentIndex].name}</h3>
                  <p className="text-sm opacity-75 mb-2">
                    {trainings[currentIndex].position} - {trainings[currentIndex].company}
                  </p>
                  {trainings[currentIndex].duration && (
                    <p className="text-xs opacity-60">{trainings[currentIndex].duration}</p>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button
            onClick={() => paginate(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 btn btn-circle btn-ghost bg-base-100"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <button
            onClick={() => paginate(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 btn btn-circle btn-ghost bg-base-100"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>

          <div className="flex justify-center mt-8 gap-2">
            {trainings.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const direction = index > currentIndex ? 1 : -1;
                  setDirection(direction);
                  setCurrentIndex(index);
                }}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentIndex ? 'bg-primary' : 'bg-base-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Trainings;
